import React, {useEffect, useState} from 'react';
import TextInput from '../../../../../uiToolkit/Inputs/TextInput';
import {lessonEditorText} from '../../../lessonEditorText';
import YesNoOption from '../../../../../uiToolkit/Inputs/YesNoOption';
import {LessonContentMChoice} from '../../../../../data/models/LessonContent/LessonContentBase';
import EditorOptionsFooter from '../../../../../uiToolkit/Editor/EditorOptionsFooter';
import './styles.css';
import Button from '../../../../../uiToolkit/Buttons/Button';
import Dropdown from '../../../../../uiToolkit/Inputs/Dropdown';
import {GradeWeights} from '../../../../../data/models/LessonContent/GradeWeights';
import {BaseContainerForwardProps} from '../../BaseOptionsContainer';
import ReplaceNode from '../../ReplaceNode';

const MAX_AMOUNT_OF_ANSWERS = 4;

interface Props extends BaseContainerForwardProps {
  entityToEdit: LessonContentMChoice;
}

const MChoiceOptionsUI = React.memo(
  ({
    entityToEdit,
    onEdit,
    isLoading,
    onDelete,
    isPublished,
    onSoftDelete,
  }: Props) => {
    const [question, setQuestion] = useState('');
    const [answers, setAnswers] = useState<
      {value: string; isCorrect: boolean}[]
    >([{value: '', isCorrect: false}]);
    const [gradeWeight, setGradeWeight] = useState('1');

    useEffect(() => {
      setQuestion(entityToEdit.question);
      const newAnswers: any[] = [];
      entityToEdit.answers.forEach(item => {
        newAnswers.push({
          value: item.value,
          isCorrect: item.isCorrect,
        });
      });
      setGradeWeight(entityToEdit.gradeWeight?.toString() || '1');
      setAnswers(newAnswers);
    }, [entityToEdit]);

    const onChangeAnswer = (index: number) => (answer: string) => {
      const newAnswers = [...answers];
      newAnswers[index].value = answer;
      setAnswers(newAnswers);
    };

    const setIsCorrect = (index: number) => (value: boolean) => {
      const newAnswers = [...answers];
      newAnswers[index].isCorrect = value;
      setAnswers(newAnswers);
    };

    const onAddAnswer = () => {
      if (answers.length < MAX_AMOUNT_OF_ANSWERS) {
        const newAnswers = [...answers];
        newAnswers.push({value: '', isCorrect: false});
        setAnswers(newAnswers);
      }
    };

    const onRemove = (index: number) => {
      const newAnswers = [...answers];
      newAnswers.splice(index, 1);
      setAnswers(newAnswers);
    };

    const onSave = () => {
      if (isValid()) {
        onEdit({
          ...entityToEdit,
          question,
          answers,
          gradeWeight: parseInt(gradeWeight, 10),
        } as LessonContentMChoice);
      }
    };

    const isValid = () => {
      let allAnswersFilled = true;
      let oneAnswerCorrect = false;
      let have2sameAnswers = false;
      answers.forEach((answer, index) => {
        if (answer.value.trim() === '') {
          allAnswersFilled = false;
        }
        if (answer.isCorrect) {
          oneAnswerCorrect = true;
        }
        if (
          answers.find(
            (x, xIndex) => x.value === answer.value && xIndex !== index,
          )
        ) {
          have2sameAnswers = true;
        }
      });
      return (
        question.trim() !== '' &&
        answers.length > 0 &&
        allAnswersFilled &&
        !have2sameAnswers &&
        oneAnswerCorrect
      );
    };

    const changesWasMade = () => {
      let answersChanged = false;
      if (answers.length !== entityToEdit.answers.length) {
        return true;
      }
      if (entityToEdit.gradeWeight?.toString() !== gradeWeight) {
        return true;
      }
      answers.forEach((item, index) => {
        if (
          item.value !== entityToEdit.answers[index].value ||
          item.isCorrect !== entityToEdit.answers[index].isCorrect
        ) {
          answersChanged = true;
        }
      });
      return answersChanged || question !== entityToEdit.question;
    };

    return (
      <div className="MChoiceOptions">
        <Dropdown
          heading={lessonEditorText.gradeWeight}
          options={GradeWeights}
          setSelectedOption={setGradeWeight}
          selectedOption={gradeWeight}
          type="box"
          className="MChoiceGradeWeightInput"
        />
        <TextInput
          value={question}
          onChange={setQuestion}
          heading={lessonEditorText.question}
          placeholder={lessonEditorText.questionPlaceholder}
          maxCharacters={200}
          isMandatory
          uiType="box"
        />
        {answers.map((item, index) => (
          <div key={index.toString()}>
            <TextInput
              value={item.value}
              onChange={onChangeAnswer(index)}
              heading={index === 0 ? lessonEditorText.answerTitle : undefined}
              placeholder={lessonEditorText.answerPlaceholder}
              className="AnswersInput"
              maxCharacters={140}
              isMandatory={index === 0}
              uiType="box"
            />
            <div className="Section">
              <YesNoOption
                value={item.isCorrect}
                onSelect={setIsCorrect(index)}
                uiType="icon"
              />
              <Button
                title={lessonEditorText.deleteAnswer}
                onClick={() => onRemove(index)}
                uiType="text_danger"
                className="DeleteAnswerButton"
              />
            </div>
          </div>
        ))}
        <Button
          title={lessonEditorText.addAnswer}
          onClick={onAddAnswer}
          disabled={answers.length === MAX_AMOUNT_OF_ANSWERS}
          uiType="box"
          icon="icon-typePlus-icon"
          infoText={lessonEditorText.disclaimer}
          className="AddAnswerButton"
        />
        <ReplaceNode currentNode={entityToEdit} />
        <EditorOptionsFooter
          onSave={onSave}
          saveEnabled={changesWasMade() && isValid()}
          onDelete={onDelete}
          isLoading={isLoading}
          deleteDisabled={isPublished}
          onSoftDelete={onSoftDelete}
        />
      </div>
    );
  },
);

export default MChoiceOptionsUI;
